import React, { useEffect, useRef, useState } from 'react';

import ImagePreviewModal from '@/components/media/ImagePreviewModal.js';
import {
  fixJianyingProductionImage,
  uploadJianyingManualCrop,
} from '@/features/orders/ordering/services/orderMediaService.js';
import {
  processJianyingOrderCrop,
} from '../../../../../services/jianyingJobService.js';
import JianyingImageCard from './JianyingImageCard.js';
import JianyingManualCropModal from './JianyingManualCropModal.js';

const PROCESSING_ERROR_MESSAGES = {
  crop_in_progress: '图片正在处理中，请稍后重试。',
  forbidden: '当前账号无权处理这张图片。',
  image_changed: '图片已发生变化，请刷新后重试。',
  image_file_invalid: '仅支持 JPG、JPEG、PNG 或 WebP 图片。',
  image_not_found: '图片不存在或已被移除。',
  image_repair_failed: '图片修复失败，请稍后重试。',
  image_upload_in_progress: '图片正在上传，请稍后重试。',
};

const ACTION_FALLBACK_MESSAGES = {
  crop: '自动裁剪失败，已保留现有图片。',
  manual_crop: '保存裁剪结果失败，已保留现有图片。',
  repair: '图片修复失败，请稍后重试。',
  upload: '上传照片失败，请重试。',
};

const getErrorCode = (error) => String(
  error?.response?.data?.code
    || error?.response?.data?.error
    || error?.code
    || '',
).trim();

const getJianyingImageProcessingError = (error, action) => (
  PROCESSING_ERROR_MESSAGES[getErrorCode(error)]
  || ACTION_FALLBACK_MESSAGES[action]
  || '图片处理失败，请稍后重试。'
);

const runJianyingImageBatch = async (images, operation) => {
  const succeeded = [];
  const failed = [];
  for (const image of Array.isArray(images) ? images : []) {
    try {
      succeeded.push({ image, value: await operation(image) });
    } catch (error) {
      failed.push({ image, error });
    }
  }
  return { failed, succeeded };
};

const summarizeJianyingCropItems = (images, items) => {
  const itemsById = new Map(
    (Array.isArray(items) ? items : []).map((item) => [String(item.image_id), item]),
  );
  return (Array.isArray(images) ? images : []).reduce((summary, image) => {
    const item = itemsById.get(String(image.id));
    if (item?.status === 'succeeded' && !item.reprocess_error) {
      summary.succeeded.push({ image, value: item });
    } else {
      summary.failed.push({
        image,
        error: {
          code: item?.reprocess_error ? 'crop_reprocess_failed' : item?.error || 'crop_failed',
        },
      });
    }
    return summary;
  }, { failed: [], succeeded: [] });
};

const safeLabel = (image = {}) => String(
  image.file?.name || image.name || image.id || '未命名图片',
).replace(/[\r\n\t]+/g, ' ').trim().slice(0, 80) || '未命名图片';

const formatJianyingImageBatchNotice = (result, label, action) => {
  const succeeded = Array.isArray(result?.succeeded) ? result.succeeded : [];
  const failed = Array.isArray(result?.failed) ? result.failed : [];
  if (!failed.length) return `${label}完成，共处理 ${succeeded.length} 张图片。`;
  const visible = failed.slice(0, 3).map(({ image, error }) => (
    `${safeLabel(image)}（${getJianyingImageProcessingError(error, action)}）`
  ));
  const remaining = failed.length - visible.length;
  return `${label}完成：成功 ${succeeded.length} 张，失败 ${failed.length} 张。失败项：`
    + `${visible.join('、')}${remaining > 0 ? `，其余 ${remaining} 张` : ''}。`;
};

const JianyingImageProcessingPanel = ({
  clientId,
  disabled = false,
  images = [],
  onRefresh = async () => false,
  onUpload = () => {},
  uploadStatus = { state: 'idle' },
}) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busyActions, setBusyActions] = useState(() => new Map());
  const [manualCropImage, setManualCropImage] = useState(null);
  const [notice, setNotice] = useState('');
  const [previewTarget, setPreviewTarget] = useState(null);
  const operationGeneration = useRef(0);
  const orderedImages = Array.isArray(images) ? images : [];
  const selectedImages = orderedImages.filter((image) => selectedIds.has(String(image.id)));
  const actionableSelectedImages = selectedImages.filter((image) => (
    !busyActions.has(String(image.id))
  ));

  useEffect(() => {
    operationGeneration.current += 1;
    setSelectedIds(new Set());
    setBusyActions(new Map());
    setManualCropImage(null);
    setNotice('');
  }, [clientId]);

  const toggleSelected = (image) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      const imageId = String(image.id);
      if (next.has(imageId)) next.delete(imageId); else next.add(imageId);
      return next;
    });
  };

  const setBusy = (targets, label) => {
    setBusyActions((current) => {
      const next = new Map(current);
      targets.forEach((image) => next.set(String(image.id), label));
      return next;
    });
  };

  const clearBusy = (targets) => {
    setBusyActions((current) => {
      const next = new Map(current);
      targets.forEach((image) => next.delete(String(image.id)));
      return next;
    });
  };

  const runRepair = async (targets) => {
    const actionable = targets.filter((image) => !busyActions.has(String(image.id)));
    if (!actionable.length) return;
    const generation = operationGeneration.current;
    setBusy(actionable, '修复');
    const result = await runJianyingImageBatch(actionable, (image) => (
      fixJianyingProductionImage({ clientId, imageId: image.id })
    ));
    if (generation === operationGeneration.current) {
      await onRefresh();
      setNotice(formatJianyingImageBatchNotice(result, '图片修复', 'repair'));
      clearBusy(actionable);
    }
  };

  const runAutoCrop = async (targets) => {
    const actionable = targets.filter((image) => !busyActions.has(String(image.id)));
    if (!actionable.length) return;
    const generation = operationGeneration.current;
    setBusy(actionable, '自动裁剪');
    let result;
    try {
      const response = await processJianyingOrderCrop({
        clientId,
        force: true,
        imageIds: actionable.map((image) => image.id),
      });
      result = summarizeJianyingCropItems(actionable, response?.items);
    } catch (error) {
      result = {
        failed: actionable.map((image) => ({ image, error })),
        succeeded: [],
      };
    }
    if (generation === operationGeneration.current) {
      await onRefresh();
      setNotice(formatJianyingImageBatchNotice(result, '自动裁剪', 'crop'));
      clearBusy(actionable);
    }
  };

  const saveManualCrop = async (blob) => {
    const image = manualCropImage;
    if (!image) return;
    const generation = operationGeneration.current;
    setManualCropImage(null);
    setBusy([image], '保存裁剪');
    let result;
    try {
      const file = new File([blob], `${image.id}-crop.jpg`, { type: 'image/jpeg' });
      const value = await uploadJianyingManualCrop({ clientId, imageId: image.id, file });
      result = { failed: [], succeeded: [{ image, value }] };
    } catch (error) {
      result = { failed: [{ image, error }], succeeded: [] };
    }
    if (generation === operationGeneration.current) {
      await onRefresh();
      setNotice(formatJianyingImageBatchNotice(result, '手动裁剪', 'manual_crop'));
      clearBusy([image]);
    }
  };

  const uploading = uploadStatus.state === 'uploading';

  return (
    <section className="jianying-image-processing" aria-labelledby="jianying-image-processing-title">
      <header className="jianying-image-processing__header">
        <div>
          <h2 id="jianying-image-processing-title">图片处理</h2>
        </div>
        <label className="jianying-production-upload">
          <input
            className="visually-hidden"
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            multiple
            disabled={disabled || uploading}
            onChange={(event) => {
              if (event.target.files?.length) onUpload(event.target.files);
              event.target.value = '';
            }}
          />
          <i className="bi bi-upload" aria-hidden="true" />
          {uploading ? '上传中…' : '上传照片'}
        </label>
      </header>

      <div className="jianying-image-processing__toolbar">
        <span>已选择 {selectedImages.length} 张</span>
        <button
          type="button"
          disabled={disabled || !actionableSelectedImages.length}
          onClick={() => runRepair(actionableSelectedImages)}
        >
          批量一键修复
        </button>
        <button
          type="button"
          disabled={disabled || !actionableSelectedImages.length}
          onClick={() => runAutoCrop(actionableSelectedImages)}
        >
          批量自动裁剪
        </button>
      </div>

      {notice || uploadStatus.message ? (
        <p className="jianying-notice" role="status">{notice || uploadStatus.message}</p>
      ) : null}

      {orderedImages.length ? (
        <div className="jianying-image-processing-grid">
          {orderedImages.map((image, index) => (
            <JianyingImageCard
              key={image.id}
              busyLabel={busyActions.get(String(image.id)) || ''}
              disabled={disabled}
              entry={{ id: image.id, image, state: 'processing' }}
              index={index}
              isSelected={selectedIds.has(String(image.id))}
              mode="processing"
              onAutoCrop={(target) => runAutoCrop([target])}
              onManualProcessingCrop={setManualCropImage}
              onPreview={setPreviewTarget}
              onRepair={(target) => runRepair([target])}
              onToggleSelected={toggleSelected}
            />
          ))}
        </div>
      ) : (
        <div className="jianying-empty">该订单还没有图片。点击“上传照片”添加制作专用图片。</div>
      )}

      <JianyingManualCropModal
        show={Boolean(manualCropImage)}
        image={manualCropImage}
        onCancel={() => setManualCropImage(null)}
        onSave={saveManualCrop}
      />
      <ImagePreviewModal
        show={Boolean(previewTarget?.src)}
        src={previewTarget?.src || ''}
        alt={previewTarget?.alt || ''}
        title={previewTarget?.title || '图片预览'}
        onClose={() => setPreviewTarget(null)}
      />
    </section>
  );
};

export default JianyingImageProcessingPanel;
